import { BulkApprovalBar } from "../BulkApprovalBar";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { customGetAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import {
  scrollTop,
  getStatusFlow,
  StatusBadge,
  displayFlexItem,
} from "services/constants";
import { Apply } from "./Apply";
import { EditCheckInOut } from "pages/TeamZone/Attendance/AttendanceStatus/Status/EditCheckInOut";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { showToast } from "services/utils/Status";
import { TeamZoneOffDayDuty } from "../Headers";
import { OffDayDutyDetailPanel } from "./DetailPanel";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import {
  OffDayDutyStatusCards,
  canonicalOffDayDutyStatus,
  offDayDutyStatusVisual,
  resolveOffDayDutyDisplayStatus,
} from "./OffDayDutyStatusCards";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  bulkSelectionCheckboxSx,
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import moment from "moment";
import { resolveRangeLabel } from "../dateRangeUtils";
import { normalizeApprovalPersonFields } from "../displayNameUtils";

const OFFDAYDUTY_LIST_DEFAULT_VIEW_ID = "__default_offdayduty_list_view__";

// Composite key — Pre-Approval's `id` and Post-Approval's `approval_id` are
// separate DB sequences and can collide numerically once merged into one
// list, so selection/row-identity can't rely on either id alone.
const rowKeyOf = (row) => `${row?.approval_mode}:${row?.id ?? row?.approval_id}`;

export const OffDayDuty = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const { domain, token, userDetails, topbarCompanyId, topbarCompanyName, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const containerRef = useRef(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [offDayDutyData, setOffDayDutyData] = useState([]);
  const [offDayDutyFilterData, setOffDayDutyFilterData] = useState([]);
  const [viewDetails, setViewDetails] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop — previously hardcoded to "Last 7 Days"
  // regardless of the actual startDate/endDate this screen was handed, on
  // a fresh ?view= deep link or Team Workspace Home's "date" param. A
  // manual re-pick on this screen's own picker still overrides it locally.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  // Retrospective (Post-Approval) rows keep opening the same shared
  // EditCheckInOut side-peek the old Post-Approval screen used (Shift Map/
  // Punch Entries tabs + the qty/payout/band-hours approval form) — only
  // Planned (Pre-Approval) rows use the new DetailPanel below.
  const [openSidePeek, setOpenSidePeek] = useState(false);
  const [shiftDetails, setShiftDetails] = useState({});
  const [departmentId, setDepartmentId] = useState("");
  const [editState, setEditState] = useState(false);
  const {
    expanded: detailPanelExpanded,
    toggleExpanded: toggleDetailPanelExpanded,
  } = useExpandablePanel(viewDetails);
  const {
    expanded: createPanelExpanded,
    toggleExpanded: toggleCreatePanelExpanded,
  } = useExpandablePanel(isCreatePanelOpen);

  useEffect(() => {
    const fetchOffDayDutyDetails = async () => {
      // Merged endpoint — replaces the old separate teamzone-pre-day-off/
      // teamzone-post-day-off GET calls with one teamworkspace-dayoff-approve/
      // call. Response is one flat list; each row should carry its own Pre/
      // Post discriminator the same way the Overtime screen's equivalent
      // merge came back with `ot_type`. TODO: the exact field/values below
      // (`record_type`/`dayoff_type`) are a provisional guess — confirm
      // against the real response and adjust once available.
      const res = await customGetAPI(
        `${domain}team_zone/approve/day_off/teamworkspace-dayoff-approve/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
      );
      const rows = Array.isArray(res?.responseDetails)
        ? res.responseDetails
        : (res?.responseDetails?.results ?? []);
      const isPostDayoff = (row) =>
        ["post", "postdayoff"].includes(
          (row?.record_type || row?.dayoff_type || "").toLowerCase(),
        );

      // getStatusFlow (services/constants) already reads these same fields
      // for the expand row's approval timeline — one column showing
      // whichever of the three actually fired for this row.
      const approverName = (row) =>
        row.approved_by || row.rejected_by || row.cancelled_by || "-";

      // Approved requests whose date has already passed read as "Completed"
      // (same rule My Workspace's Off-Day Duty screen already applies) --
      // resolved once here so every downstream count/filter/badge that reads
      // row.status sees it without having to know about the date rule.
      const plannedRows = rows
        .filter((row) => !isPostDayoff(row))
        .map((row) => ({
          ...row,
          status: resolveOffDayDutyDisplayStatus(row.status, row.date),
          approval_mode: "Planned",
          approver_name: approverName(row),
        }))
        .map(normalizeApprovalPersonFields);
      const retrospectiveRows = rows
        .filter((row) => isPostDayoff(row))
        .map((row) => ({
          ...row,
          status: resolveOffDayDutyDisplayStatus(row.status, row.date),
          approval_mode: "Retrospective",
          hideAppliedByInStatusFlow: true,
          approver_name: approverName(row),
        }))
        .map(normalizeApprovalPersonFields);

      const combinedRows = [...plannedRows, ...retrospectiveRows];
      setOffDayDutyData(combinedRows);
      // Also seed the filtered view directly — CustomFilter recomputes it
      // in its own effect a render later, so without this the table
      // flashes "No Matching Results" for a frame right as loading ends.
      setOffDayDutyFilterData(combinedRows);
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchOffDayDutyDetails();
    }
  }, [
    domain,
    endDate,
    reCallApi,
    setReCallApi,
    startDate,
    topbarCompanyId,
    userDetails?.id,
  ]);

  // Off Day Duty is a real backend submodule (unlike Permission/OnDuty/
  // CompOff/Break which bucket into "WorkAdjustment") — access resolves
  // directly against it.
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approvals",
    "Off Day Duty",
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (editState && !openSidePeek) {
      setEditState(false);
      setReCallApi(true);
    }
  }, [editState, openSidePeek, setReCallApi]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOffDayDutyFilterData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const offDayDutyStatusCounts = useMemo(
    () =>
      (offDayDutyFilterData || []).reduce((acc, request) => {
        const status = canonicalOffDayDutyStatus(request?.status);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [offDayDutyFilterData],
  );

  const visibleOffDayDutyData = useMemo(() => {
    if (!statusCardFilter) return offDayDutyFilterData;
    return offDayDutyFilterData.filter(
      (request) => canonicalOffDayDutyStatus(request?.status) === statusCardFilter,
    );
  }, [offDayDutyFilterData, statusCardFilter]);

  const handleStatusCardClick = (key) => {
    clearSelection();
    setShowBulkRejectDialog(false);
    setPage(0);
    setExpandedIndex(null);
    setExpandedData(null);
    setViewDetails(false);
    setSelectedData(null);
    setOpenSidePeek(false);
    if (key === "All") {
      setStatusCardFilter("");
    } else {
      setStatusCardFilter(key);
    }
  };

  // Bulk select/approve/reject — selection spans both Planned and
  // Retrospective rows at once; the two groups get routed to their own
  // backend endpoint/payload shape when the action fires.
  const isSelected = (row) => selectedRowKeys.has(rowKeyOf(row));

  const pendingRows = useMemo(
    () => visibleOffDayDutyData.filter((row) => row.status === "Pending"),
    [visibleOffDayDutyData],
  );

  const toggleRow = (row) => {
    if (row.status !== "Pending") return;
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      const key = rowKeyOf(row);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    const allPendingSelected =
      pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
    if (allPendingSelected) {
      setSelectedRowKeys(new Set());
    } else {
      setSelectedRowKeys(new Set(pendingRows.map((r) => rowKeyOf(r))));
    }
  };

  const isAllSelected =
    pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
  const isIndeterminate =
    selectedRowKeys.size > 0 && selectedRowKeys.size < pendingRows.length;

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkRejectReason("");
  };

  const selectedRows = visibleOffDayDutyData.filter((r) => isSelected(r));

  // Sends raw JSON bodies — customAPI spreads its payload argument
  // ({...payload}), which would turn an array into a numeric-keyed object,
  // so bulk update goes straight to fetch instead (same reason every other
  // split screen's bulk action bar does this).
  const putRaw = async (url, body) => {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify(body),
      });
      const res = await response.json();
      if (response.ok) {
        return { ok: true, code: response.status, responseDetails: res };
      }
      return {
        ok: false,
        code: response.status,
        message: res?.error || res?.errors?.[0]?.message || res?.message,
      };
    } catch (error) {
      return { ok: false, message: error.message };
    }
  };

  const callBulkAPI = async (status, reason) => {
    const plannedRows = selectedRows.filter((r) => r.approval_mode === "Planned");
    const retrospectiveRows = selectedRows.filter(
      (r) => r.approval_mode === "Retrospective",
    );

    // Merged endpoint — one PUT carries both groups: pre_dayoff_ids for
    // Planned rows, post_dayoff (each with its own approval_id/approval_type,
    // since Retrospective rows can mix Week Off and Holiday) for
    // Retrospective rows, instead of the old two separate pre/post calls.
    return putRaw(`${domain}team_zone/approve/day_off/teamworkspace-dayoff-approve/?company_id=${topbarCompanyId}`, {
      pre_dayoff_ids: plannedRows.map((row) => row.id),
      post_dayoff: retrospectiveRows.map((row) => ({
        approval_id: row.approval_id,
        approval_type: row.approval_type || row.day_type || "Week Off",
      })),
      status,
      ...(status === "Rejected" && { rejected_reason: reason || "" }),
    });
  };

  // The merged PUT answers 200 even when only part of the batch went
  // through; the API flags that as `partial` and its message says what failed.
  const showBulkResultToast = (response) => {
    const details = response?.responseDetails || {};
    showToast(details.partial ? "error" : "success", details.message);
  };

  const handleBulkApprove = async () => {
    if (!selectedRows.length) return;
    setBulkLoading(true);
    const response = await callBulkAPI("Approved");
    if (response?.ok) {
      showBulkResultToast(response);
      clearSelection();
      setReCallApi(true);
    } else {
      // putRaw (unlike customAPI) doesn't toast on its own.
      showToast("error", response?.message);
    }
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (!selectedRows.length) return;
    setBulkLoading(true);
    const response = await callBulkAPI("Rejected", bulkRejectReason);
    if (response?.ok) {
      showBulkResultToast(response);
      clearSelection();
      setShowBulkRejectDialog(false);
      setReCallApi(true);
    } else {
      // putRaw (unlike customAPI) doesn't toast on its own.
      showToast("error", response?.message);
    }
    setBulkLoading(false);
  };

  const handleOffDayDutyDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const offDayDutyColumnHeaders = useMemo(
    () => TeamZoneOffDayDuty.filter((header) => header.key !== "expand"),
    [],
  );

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_offday_duty_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: OFFDAYDUTY_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || OFFDAYDUTY_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== OFFDAYDUTY_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(
    () => offDayDutyColumnHeaders,
    [offDayDutyColumnHeaders],
  );

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find((view) => view.id === OFFDAYDUTY_LIST_DEFAULT_VIEW_ID) ||
      null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () => savedViewsForScope.filter((view) => view.id !== OFFDAYDUTY_LIST_DEFAULT_VIEW_ID),
    [savedViewsForScope],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) {
      // Whatever headerConfig the user saved is authoritative — same as any
      // other saved view. (Previously this back-filled any header missing
      // from headerConfig, meant to surface columns added to the schema
      // after the view was saved — but that's indistinguishable from a
      // column the user deliberately unchecked, so it silently re-added
      // every unchecked header right back on the next render.)
      return storedDefaultView;
    }

    return {
      id: OFFDAYDUTY_LIST_DEFAULT_VIEW_ID,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({
        key: header.key,
        name: header.name,
      })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === OFFDAYDUTY_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }
    return (
      customSavedViews.find((view) => view.id === effectiveViewId) || defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      offDayDutyColumnHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return { ...baseHeader, name: entry.name?.trim() || baseHeader.name };
        })
        .filter(Boolean);
      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = offDayDutyColumnHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, offDayDutyColumnHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(OFFDAYDUTY_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === OFFDAYDUTY_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(OFFDAYDUTY_LIST_DEFAULT_VIEW_ID);
        return;
      }
      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(OFFDAYDUTY_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || OFFDAYDUTY_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(OFFDAYDUTY_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={offDayDutyColumnHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={check_access}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === OFFDAYDUTY_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      offDayDutyColumnHeaders,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, topbarCompanyName],
  );

  const newRequestButton = (
    <NewRequestButton
      hasAccess={check_access}
      deniedMessage="Access denied: You are not authorized to create an off-day duty request"
      onClick={() => setIsCreatePanelOpen(true)}
    />
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>Off-Day Duty</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and approve week off and holiday duty requests
            </Mui.Typography>
          </Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleOffDayDutyDateRange}
              includeCurrentDate
              showToday
            />
            {newRequestButton}
          </Mui.Stack>
        </Box>

        <OffDayDutyStatusCards
          counts={offDayDutyStatusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title={"Off-Day Duty Requests"}
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={offDayDutyData}
            filteredData={visibleOffDayDutyData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
             labeledToolbar={true}
          />
        </Box>
        <BulkApprovalBar
          selectedCount={statusCardFilter === "Pending" ? selectedRowKeys.size : 0}
          loading={bulkLoading}
          onReject={() => setShowBulkRejectDialog(true)}
          onApprove={handleBulkApprove}
        />
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable stickyHeader sx={{ minWidth: 920, width: "100%", tableLayout: "fixed" }}>
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell
                      padding="checkbox"
                      sx={{ ...approveTableHeadCell, top: APP_BAR_OFFSET + customFilterHeight }}
                    >
                      {statusCardFilter === "Pending" && pendingRows.length > 0 && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={toggleAll}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  <Mui.TableCell
                    sx={{ ...approveTableHeadCell, top: APP_BAR_OFFSET + customFilterHeight, width: 40 }}
                  />
                  {visibleHeaders?.map((value) => (
                    <Mui.TableCell
                      key={value.key}
                      sx={{ ...approveTableHeadCell, top: APP_BAR_OFFSET + customFilterHeight }}
                    >
                      <TruncatedTableValue value={value?.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleOffDayDutyData?.length === 0 ? (
                  <TableEmptyState
                    dataLength={offDayDutyData}
                    colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                  />
                ) : (
                  visibleOffDayDutyData
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    ?.map((value, index) => (
                      <React.Fragment key={rowKeyOf(value)}>
                        <Mui.TableRow
                          onClick={() => {
                            if (value.approval_mode === "Retrospective") {
                              setOpenSidePeek(true);
                              setShiftDetails({
                                user_id: value?.user_id,
                                shift_filter_date: value?.date
                                  ? value.date.split("-").reverse().join("-")
                                  : null,
                                date: value?.date,
                                user_name: value?.employee_name,
                                shift_name: value?.shift,
                                latest_pay_date: value?.last_pay_date
                                  ? value.last_pay_date.split("-").reverse().join("-")
                                  : null,
                                type: value?.status,
                              });
                              setDepartmentId(value?.department_id);
                              setExpandedIndex(null);
                              return;
                            }
                            setSelectedData(value);
                            setViewDetails(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            ...(viewDetails && rowKeyOf(value) === rowKeyOf(selectedData || {})
                              ? designShell.neutralSelectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {statusCardFilter === "Pending" && (
                            <Mui.TableCell
                              padding="checkbox"
                              sx={approveTableBodyCell}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {statusCardFilter === "Pending" && value.status === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={isSelected(value)}
                                  onChange={() => toggleRow(value)}
                                />
                              )}
                            </Mui.TableCell>
                          )}
                          <Mui.TableCell
                            sx={approveTableBodyCell}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedIndex((prev) => (prev === index ? null : index));
                              setExpandedData(value);
                            }}
                          >
                            <ArrowRightIcon
                              sx={{
                                color: designColors.slate500,
                                cursor: "pointer",
                                transition: "transform 0.2s ease",
                                transform:
                                  expandedIndex === index ? "rotate(90deg)" : "rotate(0deg)",
                              }}
                            />
                          </Mui.TableCell>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell key={head.key} data-label={head.name} sx={approveTableBodyCell}>
                              {head.key === "employee_name" ? (
                                <Mui.Stack direction="row" spacing={1} alignItems="center">
                                  <Mui.Avatar
                                    sx={{ width: 28, height: 28 }}
                                    alt={value?.employee_name?.charAt(0).toUpperCase()}
                                    src={value?.image_url}
                                  />
                                  <Mui.Typography
                                    sx={{
                                      fontWeight:
                                        viewDetails && rowKeyOf(value) === rowKeyOf(selectedData || {})
                                          ? 700
                                          : 500,
                                      color:
                                        viewDetails && rowKeyOf(value) === rowKeyOf(selectedData || {})
                                          ? designColors.slate900
                                          : "inherit",
                                      fontSize: 14,
                                    }}
                                  >
                                    {value?.employee_name || "N/A"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              ) : head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    color={
                                      value?.color_code ||
                                      offDayDutyStatusVisual(value?.status).accent
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : head.key === "approval_mode" ? (
                                <Mui.Chip
                                  size="small"
                                  label={value?.approval_mode || "N/A"}
                                  sx={{
                                    height: 24,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    backgroundColor:
                                      value?.approval_mode === "Planned" ? "#EFF6FF" : "#FFFBEB",
                                    color:
                                      value?.approval_mode === "Planned" ? "#2563EB" : "#D97706",
                                  }}
                                />
                              ) : (
                                (<TruncatedTableValue value={value[head.key] ?? "N/A"} />)
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>

                        {expandedIndex === index && (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                              sx={{ p: 2, borderBottom: `1px solid ${designColors.slate200}` }}
                            >
                              {getStatusFlow(expandedData)[
                                // getStatusFlow only knows Pending/Approved/
                                // Rejected/Cancelled -- "Completed" is the
                                // same approval timeline as Approved, just
                                // past-dated, so look it up under that key.
                                expandedData?.status === "Completed"
                                  ? "Approved"
                                  : expandedData?.status
                              ]?.map(
                                (item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ),
                              )}
                              <ApprovalAuditTrail record={expandedData} compact />
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleOffDayDutyData?.length || 0,
              itemLabel: "off-day duty requests",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>
      <Apply
        open={isCreatePanelOpen}
        onClose={() => {
          handleCreateClosePanel();
        }}
        setReCallApi={setReCallApi}
        expanded={createPanelExpanded}
        onToggleExpand={toggleCreatePanelExpanded}
      />
      <OffDayDutyDetailPanel
        open={viewDetails}
        onClose={() => setViewDetails(false)}
        request={selectedData}
        checkAccess={check_access}
        setReCallApi={setReCallApi}
        expanded={detailPanelExpanded}
        onToggleExpand={toggleDetailPanelExpanded}
      />
      {openSidePeek ? (
        <EditCheckInOut
          setOpenSidePeek={setOpenSidePeek}
          openSidePeek={openSidePeek}
          shiftDetails={shiftDetails}
          setEditState={setEditState}
          departmentId={departmentId}
          reCallApiOffDayDuty={setReCallApi}
        />
      ) : null}
      <GlobalConfirmDialog
        open={showBulkRejectDialog}
        title="Reject Selected Requests"
        onCancel={() => {
          setShowBulkRejectDialog(false);
          setBulkRejectReason("");
        }}
        onConfirm={handleBulkReject}
        confirmLabel="Reject"
        cancelLabel="Cancel"
        loading={bulkLoading}
        danger
        maxWidth="sm"
        reasonValue={bulkRejectReason}
        onReasonChange={setBulkRejectReason}
        reasonPlaceholder="Enter a reason for rejection"
        reasonMinRows={2}
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Reason (optional) - applied to all selected requests.
        </Mui.Typography>
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};
